import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { UtilService } from "src/app/util/util.service";

enum ViewType {
  GRID = "grid",
  LIST = "list",
}

@Component({
  selector: "lhpe-audience-list",
  templateUrl: "./audience-list.component.html",
  styleUrls: ["./audience-list.component.scss"],
})
export class AudienceListComponent implements OnInit {
  selectedView: ViewType = ViewType.LIST;
  searchValue = "";

  columns = [
    {
      name: "Name",
      code: "name",
      filter: true,
      selected: true,
    },
    {
      name: "Created",
      code: "created_at",
      filter: true,
      selected: true,
    },
    {
      name: "Created by",
      code: "created_by_name",
      filter: true,
      selected: true,
    },

    {
      name: "Last modified",
      code: "modified_at",
      filter: true,
      selected: true,
    },
    {
      name: "Modified by",
      code: "modified_by_name",
      filter: true,
      selected: true,
    },
    {
      name: "Total Broadcasts",
      code: "total_broadcasts",
      filter: true,
      selected: true,
    },
    {
      name: "Last broadcast",
      code: "last_broadcast_name",
      filter: true,
      selected: true,
    },
    {
      code: "options",
    },
  ];
  displayedColumns = this.columns.map((column) => column.code);
  sortColumns = this.columns.filter((column) => column.filter);

  sortFormGroup = new FormGroup({
    sort: new FormControl(""),
  });
  audiences!: any[];
  filteredAudiences!: any[];
  audienceDataSource!: MatTableDataSource<any>;
  @ViewChild("audienceTable") audienceTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;

  //If component is being called from customer/customerLists
  @Input() fromCustomer?: boolean;
  hasBroadcastWrite: boolean = false;
  hasBroadcastDelete: boolean = false;
  hasBroadcastList: boolean = false;
  hasCategoriesList: boolean = false;
  hasTemplatesList: boolean = false;
  private onComponentDestroy = new Subject();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private AppService: AppService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private utilSvc: UtilService,
  ) {}

  ngOnInit(): void {

    this.sortFormGroup.controls.sort.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((sortBy) => {
        this.audiences = this.audiences.sort((a, b) => {
          let aValue = a[sortBy];
          let bValue = b[sortBy];

          if (aValue?.toLowerCase && bValue?.toLowerCase) {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (aValue < bValue) {
            return -1;
          }
          if (aValue > bValue) {
            return 1;
          }
          return 0;
        });
      });

    this.audiences = this.route.snapshot.data.audiences.records;

    this.filteredAudiences = this.audiences;
    this.audienceDataSource = new MatTableDataSource(this.audiences);

    const selectedFilterColumns = this.columns
      .filter((column) => column.selected)
      .map((column) => column.code);

    this.audienceDataSource.filterPredicate = (data: any, filter: string) => {
      for (let selectedColumn of selectedFilterColumns) {
        if (data[selectedColumn]?.toString().toLowerCase().includes(filter)) {
          return true;
        }
      }
      return false;
    };

    this.hasBroadcastWrite =
      this.route.snapshot.data.permissions.hasBroadcastWrite;
    this.hasBroadcastDelete =
      this.route.snapshot.data.permissions.hasBroadcastDelete;
    this.hasBroadcastList =
      this.route.snapshot.data.permissions.hasBroadcastList;
    this.hasCategoriesList =
      this.route.snapshot.data.permissions.hasCategoriesList;
    this.hasTemplatesList =
      this.route.snapshot.data.permissions.hasTemplatesList;
  }

  ngAfterViewInit() {
    this.audienceTable.renderRows();
    this.audienceDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get currentApp() {
    return this.SessionService.currentAppValue!;
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  changeLayout(viewType: string) {
    if (viewType === "grid") {
      this.selectedView = ViewType.GRID;
    } else if (viewType === "list") {
      this.selectedView = ViewType.LIST;
    }
  }

  applyFilter() {
    const searchValue = this.searchValue.trim().toLowerCase();

    this.audienceDataSource.filter = searchValue;

    const selectedFilterColumns = this.columns
      .filter((column) => column.selected)
      .map((column) => column.code);

    this.filteredAudiences = this.audiences.filter((audience) => {
      for (let selectedColumn of selectedFilterColumns) {
        if (
          audience[selectedColumn]
            ?.toString()
            .toLowerCase()
            .includes(searchValue)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.applyFilter();
        }
      });
  }

  goToAudience(audience: any) {
    this.router.navigate(["broadcasts", "audiences", audience.id]);
  }

  editAudience(audience: any) {
    this.router.navigate(["broadcasts", "audiences", audience.id]);
  }

  createNewAudience() {
    this.router.navigate(["broadcasts", "audiences", "create"]);
  }

  viewAudience(audience: any) {
    this.router.navigate(["broadcasts", "audiences", "view", audience.id]);
  }

  deleteAudience(audience: any) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this audience ( ${audience.name} )?`,
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.AppService.deleteAudience(
                this.currentAccount?.accountID,
                this.currentApp.id,
                audience.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar(
                  "Audience deleted",
                );
                this.uiService
                  .waitForEvent(
                    this.AppService.getAudiences(
                      this.currentAccount.accountID,
                      this.currentApp.id,
                      {
                        page: 0,
                        perPage: 999999999,
                      },
                    ),
                  )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe(
                    (audiences) => {
                      this.audiences = (audiences as any).records;

                      if (this.sortFormGroup.controls.sort.value) {
                        this.audiences = this.audiences.sort((a, b) => {
                          let aValue =
                            a[this.sortFormGroup.controls.sort.value];
                          let bValue =
                            b[this.sortFormGroup.controls.sort.value];

                          if (aValue?.toLowerCase && bValue?.toLowerCase) {
                            aValue = aValue.toLowerCase();
                            bValue = bValue.toLowerCase();
                          }

                          if (aValue < bValue) {
                            return -1;
                          }
                          if (aValue > bValue) {
                            return 1;
                          }
                          return 0;
                        });
                      }

                      this.filteredAudiences = this.audiences;
                      this.audienceDataSource = new MatTableDataSource(
                        this.audiences,
                      );
                    },
                    () => {
                      this.NotificationService.openErrorSnackBar(
                        "Error retrieving audiences",
                      );
                    },
                  );
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting audience",
                );
              },
            );
        }
      });
  }

  sendBroadcastToList(audience: any) {
    this.router.navigate(["broadcasts", "create"], {
      state: { audience },
    });
  }

  get showEdit() {
    return this.hasBroadcastWrite;
  }

  get showDelete() {
    return this.hasBroadcastDelete;
  }

  get showNewAudience() {
    return this.hasBroadcastWrite;
  }

  get showSendBroadcast() {
    return (
      this.hasBroadcastWrite &&
      this.hasBroadcastList &&
      this.hasCategoriesList &&
      this.hasTemplatesList
    );
  }
}
